import { useState } from 'react'
import { FormPageSpec, OptionsPageSpec, QuestionSetPart } from './aiAssistantModel'
import { LookupQuery, LookupResult } from './aiAssistantApi'
import { FormValues, clearDependentLookups } from './formRequestModel'
import {
  QuestionSetAnswers,
  allPagesComplete,
  buildSubmitAnswers,
  initialAnswers,
  isPageComplete,
  pageAnswerSummary,
  resolvedPageAnswer,
  shownPageErrors,
} from './questionSetModel'
import FormFields from './FormFields'
import OptionList from './OptionList'
import { historyCaptionStyle } from './historyStyles'

type Props = {
  part: QuestionSetPart
  interactive: boolean
  onSubmit: (questionSetId: string, answers: Record<string, unknown>) => void
  // Searches a lookup field's entity via the chat lookup endpoint. Optional so
  // sets without lookup fields need no wiring.
  onLookupSearch?: (query: LookupQuery) => Promise<LookupResult>
  onLookupCreateRequest?: (entity: string, query: string) => void
}

const navButtonStyle = (enabled: boolean, primary: boolean) => ({
  padding: '6px 16px',
  borderRadius: 8,
  border: primary ? '1px solid #1677ff' : '1px solid #d9d9d9',
  background: primary && enabled ? '#1677ff' : '#fff',
  color: primary ? (enabled ? '#fff' : '#999') : enabled ? '#333' : '#999',
  cursor: enabled ? 'pointer' : 'default',
  font: 'inherit',
})

/** An answered set, replayed from history: every page, with its answer. */
const AnsweredQuestionSet = ({ part }: { part: QuestionSetPart }) => {
  const resolution = part.resolution ?? {}

  return (
    <div style={{ margin: '8px 0' }}>
      <p style={{ margin: '0 0 8px', fontWeight: 500 }}>{part.prompt}</p>
      {part.pages.map((page) => {
        const answer = resolvedPageAnswer(page, resolution[page.page_id])
        return (
          <div key={page.page_id} style={{ marginBottom: 8 }}>
            <p style={{ margin: '0 0 2px', color: '#666' }}>{page.prompt}</p>
            {answer.kind === 'skipped' && <p style={{ margin: 0 }}>Skipped</p>}
            {answer.kind === 'option' && <p style={{ margin: 0 }}>{answer.label}</p>}
            {answer.kind === 'form' &&
              answer.rows.map((row) => (
                <p key={row.label} style={{ margin: 0 }}>
                  <span style={{ color: '#666' }}>{row.label}: </span>
                  {row.value}
                </p>
              ))}
          </div>
        )
      })}
      <p style={historyCaptionStyle}>Answered</p>
    </div>
  )
}

/**
 * The paged multi-question control: one existing-style control per page
 * (an option list or a field set), progress chrome, and a final review page
 * with a single batch submit. All answers live in local state until Submit —
 * nothing is posted per page. Answers to skipped form pages are kept, so
 * un-skipping restores what was typed.
 */
const QuestionSet = ({
  part,
  interactive,
  onSubmit,
  onLookupSearch,
  onLookupCreateRequest,
}: Props) => {
  const pageCount = part.pages.length
  // pageIndex === pageCount is the review page.
  const [pageIndex, setPageIndex] = useState(0)
  const [answers, setAnswers] = useState<QuestionSetAnswers>(() =>
    initialAnswers(part.pages)
  )
  const [skipped, setSkipped] = useState<Set<string>>(new Set())
  const [touched, setTouched] = useState<Record<string, Set<string>>>({})
  const [cameFromReview, setCameFromReview] = useState(false)

  if (part.status === 'answered') {
    return <AnsweredQuestionSet part={part} />
  }

  const disabled = !interactive
  const onReview = pageIndex === pageCount
  const page = onReview ? null : part.pages[pageIndex]

  const unskip = (pageId: string) =>
    setSkipped((prev) => {
      if (!prev.has(pageId)) {
        return prev
      }
      const next = new Set(prev)
      next.delete(pageId)
      return next
    })

  const goNext = () => {
    if (cameFromReview) {
      setCameFromReview(false)
      setPageIndex(pageCount)
    } else {
      setPageIndex((i) => Math.min(i + 1, pageCount))
    }
  }

  const handleSkip = () => {
    if (!page || !page.skippable) {
      return
    }
    setSkipped((prev) => new Set(prev).add(page.page_id))
    goNext()
  }

  const handleEdit = (index: number) => {
    setCameFromReview(true)
    setPageIndex(index)
  }

  const handleSubmit = () => {
    if (disabled || !allPagesComplete(part.pages, answers, skipped)) {
      return
    }
    onSubmit(part.question_set_id, buildSubmitAnswers(part.pages, answers, skipped))
  }

  const canAdvance =
    !disabled && page !== null && isPageComplete(page, answers, skipped)
  const canSubmit =
    !disabled && onReview && allPagesComplete(part.pages, answers, skipped)

  return (
    <div
      style={{
        margin: '8px 0',
        border: '1px solid #d9d9d9',
        borderRadius: 8,
        padding: 12,
        background: '#f5f5f5',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 12,
        }}
      >
        <p style={{ margin: 0, fontWeight: 500 }}>{part.prompt}</p>
        <span style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>
          {onReview ? 'Review' : `Question ${pageIndex + 1} of ${pageCount}`}
        </span>
      </div>

      {page && page.kind === 'options' && (
        <OptionsPageBody
          page={page}
          disabled={disabled}
          selectedId={
            typeof answers[page.page_id] === 'string'
              ? (answers[page.page_id] as string)
              : null
          }
          onSelect={(optionId) => {
            unskip(page.page_id)
            setAnswers((prev) => ({ ...prev, [page.page_id]: optionId }))
          }}
        />
      )}

      {page && page.kind === 'form' && (
        <FormPageBody
          questionSetId={part.question_set_id}
          page={page}
          disabled={disabled}
          values={(answers[page.page_id] ?? {}) as FormValues}
          errors={shownPageErrors(page, answers, touched[page.page_id] ?? new Set())}
          onLookupSearch={onLookupSearch}
          onLookupCreateRequest={onLookupCreateRequest}
          onChange={(name, value) => {
            unskip(page.page_id)
            setAnswers((prev) => {
              const pageValues = {
                ...((prev[page.page_id] ?? {}) as FormValues),
                [name]: value,
              }
              return {
                ...prev,
                [page.page_id]: clearDependentLookups(
                  page.fields,
                  pageValues,
                  name
                ),
              }
            })
          }}
          onBlur={(name) =>
            setTouched((prev) => {
              const current = prev[page.page_id] ?? new Set<string>()
              if (current.has(name)) {
                return prev
              }
              return { ...prev, [page.page_id]: new Set(current).add(name) }
            })
          }
        />
      )}

      {onReview && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ margin: 0, fontWeight: 500, fontSize: 13 }}>
            Review your answers
          </p>
          {part.pages.map((p, i) => (
            <div
              key={p.page_id}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 8,
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid #d9d9d9',
                background: '#fff',
              }}
            >
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontSize: 12, color: '#888' }}>
                  {p.prompt}
                </span>
                <span>{pageAnswerSummary(p, answers, skipped)}</span>
              </span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => handleEdit(i)}
                style={{
                  border: 'none',
                  background: 'none',
                  color: disabled ? '#999' : '#1677ff',
                  cursor: disabled ? 'default' : 'pointer',
                  font: 'inherit',
                  fontSize: 13,
                  padding: 0,
                }}
              >
                Edit
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          type="button"
          disabled={disabled || pageIndex === 0}
          onClick={() => {
            setCameFromReview(false)
            setPageIndex((i) => Math.max(i - 1, 0))
          }}
          style={navButtonStyle(!disabled && pageIndex > 0, false)}
        >
          Back
        </button>
        <span style={{ flex: 1 }} />
        {page && page.skippable && (
          <button
            type="button"
            disabled={disabled}
            onClick={handleSkip}
            style={navButtonStyle(!disabled, false)}
          >
            Skip
          </button>
        )}
        {onReview ? (
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            style={navButtonStyle(canSubmit, true)}
          >
            Submit
          </button>
        ) : (
          <button
            type="button"
            disabled={!canAdvance}
            onClick={goNext}
            style={navButtonStyle(canAdvance, true)}
          >
            Next
          </button>
        )}
      </div>
      {/* Historical and unanswered: the user typed a new message instead. */}
      {(part.status === 'abandoned' || part.status === 'pending') && (
        <p style={historyCaptionStyle}>Not answered.</p>
      )}
    </div>
  )
}

type OptionsPageBodyProps = {
  page: OptionsPageSpec
  disabled: boolean
  selectedId: string | null
  onSelect: (optionId: string) => void
}

const OptionsPageBody = ({
  page,
  disabled,
  selectedId,
  onSelect,
}: OptionsPageBodyProps) => (
  <div>
    <p style={{ margin: '0 0 8px', fontWeight: 500, fontSize: 13 }}>{page.prompt}</p>
    <OptionList
      options={page.options}
      disabled={disabled}
      selectedId={selectedId}
      onSelect={(option) => onSelect(option.id)}
    />
  </div>
)

type FormPageBodyProps = {
  questionSetId: string
  page: FormPageSpec
  disabled: boolean
  values: FormValues
  errors: Record<string, string>
  onChange: (name: string, value: unknown) => void
  onBlur: (name: string) => void
  onLookupSearch?: (query: LookupQuery) => Promise<LookupResult>
  onLookupCreateRequest?: (entity: string, query: string) => void
}

const FormPageBody = ({
  questionSetId,
  page,
  disabled,
  values,
  errors,
  onChange,
  onBlur,
  onLookupSearch,
  onLookupCreateRequest,
}: FormPageBodyProps) => (
  <div>
    <p style={{ margin: '0 0 8px', fontWeight: 500, fontSize: 13 }}>{page.prompt}</p>
    <FormFields
      idPrefix={`${questionSetId}-${page.page_id}`}
      fields={page.fields}
      values={values}
      disabled={disabled}
      errors={errors}
      onChange={onChange}
      onBlur={onBlur}
      onLookupSearch={
        onLookupSearch
          ? (field, q, parentValue) =>
              onLookupSearch({
                questionSetId,
                pageId: page.page_id,
                field,
                q,
                parentValue,
              })
          : undefined
      }
      onLookupCreateRequest={onLookupCreateRequest}
    />
  </div>
)

export default QuestionSet
